import { validateField } from '@rammetec/data-access';
import { ADDRESS_PREFIX, addressFieldName, isAddressEmpty, pickAddress, } from '../address/postalAddress.js';
import { addressFieldDescriptors } from '../descriptors/_addressFields.js';
import { mergeErrors } from './mergeErrors.js';
/**
 * The minimum a started address must carry. Chosen so the result is a postable
 * address: a street with no number and no city is not one.
 */
const DEFAULT_REQUIRED_KEYS = [
    'zipCode',
    'street',
    'number',
    'city',
    'state',
];
/** One message per key, in the vocabulary of the screen (guia §7). */
const REQUIRED_MESSAGES = {
    zipCode: 'Informe o CEP.',
    street: 'Informe o logradouro.',
    number: 'Informe o número.',
    complement: 'Informe o complemento.',
    district: 'Informe o bairro.',
    city: 'Informe a cidade.',
    cityId: 'Escolha a cidade na lista.',
    state: 'Informe o estado.',
    stateId: 'Escolha o estado na lista.',
};
/**
 * L0 + L1 validation for the address block of a flat entity (spec 0047 D16).
 *
 * The whole subtlety is the first line: **a block that is entirely empty has no
 * errors.** Address is optional on almost every record, so demanding a CEP from
 * someone who never opened the section would make the block unusable. Once any
 * one field is filled, the block is "started" and owes `requiredKeys`.
 *
 * The shape is the house one: `mergeErrors` over a descriptor pass plus the
 * rules the descriptor cannot express. The descriptor pass runs against
 * `addressFieldDescriptors(prefix)` rather than a registered entity descriptor,
 * because the block belongs to no entity of its own (D14).
 */
export function validateAddress(entity, options = {}) {
    const prefix = options.prefix ?? ADDRESS_PREFIX;
    const address = pickAddress(entity, prefix);
    if (isAddressEmpty(address))
        return undefined;
    const requiredKeys = [...(options.requiredKeys ?? DEFAULT_REQUIRED_KEYS)];
    // Reference mode is "chosen from the catalog", so the ids are part of the
    // minimum — the text alone would leave the record unlinkable.
    if (options.mode === 'reference')
        requiredKeys.push('cityId', 'stateId');
    const missing = {};
    for (const key of requiredKeys) {
        if ((address[key] ?? '').trim() !== '')
            continue;
        missing[addressFieldName(prefix, key)] = REQUIRED_MESSAGES[key];
    }
    const record = entity;
    const shape = {};
    for (const field of addressFieldDescriptors(prefix)) {
        const message = validateField(field, record[field.name]);
        if (message)
            shape[field.name] = message;
    }
    // `missing` first: an empty value never trips a shape constraint, so the two
    // maps are disjoint — the order only fixes the intent if that ever changes.
    return mergeErrors(missing, shape);
}
//# sourceMappingURL=validateAddress.js.map